import { call, put, select, takeLatest } from "redux-saga/effects";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import { AnyEntity, EntityType } from "@mapmycustomers/shared/types/entity";
import Organization from "@mapmycustomers/shared/types/Organization";
import { FilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import localSettings from "@app/config/LocalSettings";
import getDefaultDrillDownColumns from "@app/scene/dashboard/store/utils/getDefaultDrillDownColumns";
import DrillDownViewState from "@app/scene/dashboard/types/DrillDownViewState";
import { callApi } from "@app/store/api/callApi";
import { fetchRecordsCadenceEffectivenessCardDataHelper } from "@app/store/dashboard/cardDataFetchHelpers";
import { fetchMethodByEntityType } from "@app/store/dashboard/common";
import getBandCadenceRange from "@app/store/dashboard/recordsCadenceEffectiveness/getBandCadenceRange";
import { handleError } from "@app/store/errors/actions";
import { exportEntities } from "@app/store/exportEntities/actions";
import { getOrganizationId } from "@app/store/iam";
import EntityTypesSupportedInRecordsCadenceEffectiveness from "@app/types/dashboard/cards/recordsCadenceEffectiveness/EntityTypesSupportedInRecordsCadenceEffectiveness";
import RecordsCadenceEffectivenessBand from "@app/types/dashboard/cards/recordsCadenceEffectiveness/RecordsCadenceEffectivenessBand";
import RecordsCadenceEffectivenessCardConfiguration from "@app/types/dashboard/cards/recordsCadenceEffectiveness/RecordsCadenceEffectivenessCardConfiguration";
import CommonFieldName from "@app/util/fieldModel/CommonFieldName";
import { CompanyFieldName } from "@app/util/fieldModel/CompanyFieldModel";
import { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import FieldModel from "@app/util/fieldModel/impl/FieldModel";
import { PersonFieldName } from "@app/util/fieldModel/PersonFieldModel";
import {
  createAndCondition,
  createEmptyFilterModel,
  createSimpleCondition,
} from "@app/util/filters/utils";
import loggingService from "@app/util/logging";
import { takeLatestBy } from "@app/util/redux/takeLatestBy";
import { convertFromPlatformFilterModel } from "@app/util/viewModel/convertFromPlatformFilterModel";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import {
  applyRecordsCadenceEffectivenessDrillDownListViewSettings,
  exportRecordsCadenceEffectivenessCardDrillDownData,
  fetchRecordsCadenceEffectivenessCardData,
  fetchRecordsCadenceEffectivenessCardDrillDownData,
  showRecordsCadenceEffectivenessDrillDown,
} from "./actions";
import {
  getRecordsCadenceEffectivenessDrillDownBand,
  getRecordsCadenceEffectivenessDrillDownConfiguration,
  getRecordsCadenceEffectivenessDrillDownTotalFilteredRecords,
  getRecordsCadenceEffectivenessDrillDownViewState,
} from "./selectors";

const defaultColumns: Record<EntityTypesSupportedInRecordsCadenceEffectiveness, string[]> = {
  [EntityType.COMPANY]: [
    CompanyFieldName.NAME,
    CompanyFieldName.USER,
    CompanyFieldName.ADDRESS,
    CompanyFieldName.SOURCE,
    CompanyFieldName.FREQUENCY_INTERVAL,
    CompanyFieldName.FREQUENCY_STATUS,
  ],
  [EntityType.DEAL]: [
    DealFieldName.NAME,
    DealFieldName.USER,
    DealFieldName.ADDRESS,
    DealFieldName.SOURCE,
    DealFieldName.FREQUENCY_INTERVAL,
    DealFieldName.FREQUENCY_STATUS,
  ],
  [EntityType.PERSON]: [
    PersonFieldName.NAME,
    PersonFieldName.USER,
    PersonFieldName.ADDRESS,
    PersonFieldName.SOURCE,
    PersonFieldName.FREQUENCY_INTERVAL,
    PersonFieldName.FREQUENCY_STATUS,
  ],
};

const defaultSortColumn: Record<EntityTypesSupportedInRecordsCadenceEffectiveness, string> = {
  [EntityType.COMPANY]: CompanyFieldName.FREQUENCY_STATUS,
  [EntityType.DEAL]: DealFieldName.FREQUENCY_STATUS,
  [EntityType.PERSON]: PersonFieldName.FREQUENCY_STATUS,
};

export function* onFetchRecordsCadenceEffectivenessCardData({
  payload,
}: ReturnType<typeof fetchRecordsCadenceEffectivenessCardData>) {
  const { callback, configuration, failureCallback, scope } = payload;

  try {
    yield call(fetchRecordsCadenceEffectivenessCardDataHelper, { callback, configuration, scope });
  } catch (error) {
    yield put(handleError({ error }));
    if (failureCallback) {
      yield call(failureCallback);
    }
  }
}

function* onOpenDrillDown({
  payload,
}: ReturnType<typeof showRecordsCadenceEffectivenessDrillDown.request>) {
  const { configuration, scope } = payload;
  const { criteria, extraFilters } = configuration;

  const drillDownViewState: DrillDownViewState = yield select(
    getRecordsCadenceEffectivenessDrillDownViewState
  );
  const entityType = criteria.entityType;
  const fieldModel = getFieldModelByEntityType(entityType);
  const viewState = localSettings.getViewSettings(
    `dashboard/recordsCadenceEffectiveness/${entityType}`,
    fieldModel,
    {
      ...drillDownViewState,
      columns: getDefaultDrillDownColumns(defaultColumns[entityType], fieldModel),
      sort: [
        { field: fieldModel.getByName(defaultSortColumn[entityType])!, order: SortOrder.DESC },
      ],
    }
  );

  // The band's `cadenceDaysOut` range is injected server-side at fetch time (see
  // onFetchDrillDownData); the seeded filter only carries scope + source + extra filters.
  const isTeamScope = !!scope?.teamId && !scope?.userId;
  const filter: FilterModel = createAndCondition([
    convertFromPlatformFilterModel(extraFilters ?? {}, fieldModel),
  ]);
  if (isTeamScope) {
    filter.conditions.push(
      createSimpleCondition(CommonFieldName.TEAM, FilterOperator.IN_ANY, [scope.teamId!])
    );
  }
  if (scope?.userId) {
    filter.conditions.push(
      createSimpleCondition(CommonFieldName.USER, FilterOperator.IN_ANY, [scope.userId])
    );
  }
  if (criteria.sources.length) {
    filter.conditions.push(
      createSimpleCondition(CommonFieldName.SOURCE, FilterOperator.IN_ANY, criteria.sources)
    );
  }
  viewState.filter = filter;

  yield put(showRecordsCadenceEffectivenessDrillDown.success({ viewState }));
}

export function* onFetchDrillDownData({
  payload,
}: ReturnType<typeof fetchRecordsCadenceEffectivenessCardDrillDownData.request>) {
  try {
    loggingService.debug("Dashboard: records cadence effectiveness, onFetchDrillDownData", payload);
    // Clone so we never mutate the store-owned action payload.
    const request = { ...payload.request };
    if (!payload.updateOnly) {
      // We do not listen to filter returned by AgGrid from PlatformDataSource
      delete request.filter;
    }

    if (!payload.fetchOnlyWithoutFilters) {
      yield put(applyRecordsCadenceEffectivenessDrillDownListViewSettings(request));
    }

    const configuration: RecordsCadenceEffectivenessCardConfiguration = yield select(
      getRecordsCadenceEffectivenessDrillDownConfiguration
    );
    const drillDownViewState: DrillDownViewState = yield select(
      getRecordsCadenceEffectivenessDrillDownViewState
    );
    const band: RecordsCadenceEffectivenessBand | undefined = yield select(
      getRecordsCadenceEffectivenessDrillDownBand
    );

    if (!payload.fetchOnlyWithoutFilters) {
      localSettings.setViewSettings(
        drillDownViewState,
        `dashboard/recordsCadenceEffectiveness/${configuration.criteria.entityType}`
      );
    }

    if (payload.updateOnly) {
      return;
    }

    const $offset =
      payload.fetchOnlyWithoutFilters && request.range
        ? request.range.startRow
        : drillDownViewState.range.startRow;
    const $limit =
      payload.fetchOnlyWithoutFilters && request.range
        ? request.range.endRow - request.range.startRow
        : drillDownViewState.range.endRow - drillDownViewState.range.startRow;

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const fieldModel: FieldModel = yield call(
      getFieldModelByEntityType,
      configuration.criteria.entityType
    );
    const requestPayload = {
      $filters: {
        includeAccessStatus: true,
        ...convertToPlatformFilterModel(
          payload.fetchOnlyWithoutFilters ? createEmptyFilterModel() : drillDownViewState.filter,
          drillDownViewState.columns,
          fieldModel
        ),
        // Fixed band constraint — never part of the user-editable grid filter.
        ...(band ? { cadenceDaysOut: getBandCadenceRange(band) } : {}),
      },
      $limit,
      $offset,
      $order: convertToPlatformSortModel(drillDownViewState.sort),
    };

    const fetchMethod = fetchMethodByEntityType[configuration.criteria.entityType];
    const response: ListResponse<AnyEntity> = yield callApi(fetchMethod, orgId, requestPayload);
    if (payload.dataCallback) {
      yield call(payload.dataCallback, response);
    }
    yield put(
      fetchRecordsCadenceEffectivenessCardDrillDownData.success({
        totalFilteredRecords: response.total,
        totalRecords: response.accessible,
      })
    );
  } catch (error) {
    payload.failCallback?.();
    yield put(fetchRecordsCadenceEffectivenessCardDrillDownData.failure());
    yield put(handleError({ error }));
  }
}

function* onExport() {
  const configuration: RecordsCadenceEffectivenessCardConfiguration = yield select(
    getRecordsCadenceEffectivenessDrillDownConfiguration
  );
  const drillDownViewState: DrillDownViewState = yield select(
    getRecordsCadenceEffectivenessDrillDownViewState
  );
  const total: number = yield select(getRecordsCadenceEffectivenessDrillDownTotalFilteredRecords);

  yield put(
    exportEntities.request({
      entityType: configuration.criteria.entityType,
      total,
      viewState: drillDownViewState,
    })
  );
}

export function* recordsCadenceEffectivenessSagas() {
  // takeLatestBy (keyed by cardId) because there may be several cards of this type on a board:
  // cancel a stale in-flight fetch for the same card while keeping other cards independent.
  yield takeLatestBy(
    fetchRecordsCadenceEffectivenessCardData,
    onFetchRecordsCadenceEffectivenessCardData,
    (action: ReturnType<typeof fetchRecordsCadenceEffectivenessCardData>) => action.payload.cardId
  );
  yield takeLatest(showRecordsCadenceEffectivenessDrillDown.request, onOpenDrillDown);
  yield takeLatest(fetchRecordsCadenceEffectivenessCardDrillDownData.request, onFetchDrillDownData);
  yield takeLatest(exportRecordsCadenceEffectivenessCardDrillDownData, onExport);
}
